import React from 'react';
import { Bell, Mail, Calendar, Shield } from 'lucide-react';
import { useUIStore } from '../../stores/uiStore';
import { toast } from 'react-hot-toast';

export function NotificationSettings() {
  const {
    notificationsEnabled,
    pushNotificationsEnabled,
    emailNotificationsEnabled,
    toggleNotifications,
    togglePushNotifications,
    toggleEmailNotifications,
  } = useUIStore();

  const handleToggle = (type: 'all' | 'push' | 'email') => {
    switch (type) {
      case 'all':
        toggleNotifications();
        toast.success(`Notifications ${notificationsEnabled ? 'disabled' : 'enabled'}`);
        break;
      case 'push':
        togglePushNotifications();
        toast.success(`Push notifications ${pushNotificationsEnabled ? 'disabled' : 'enabled'}`);
        break;
      case 'email':
        toggleEmailNotifications();
        toast.success(`Email notifications ${emailNotificationsEnabled ? 'disabled' : 'enabled'}`);
        break;
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Notification Settings</h2>
        <p className="mt-1 text-sm text-gray-500">
          Manage how you receive notifications
        </p>
      </div>

      <div className="divide-y divide-gray-200 rounded-lg border border-gray-200 bg-white shadow-sm">
        {/* General Notifications */}
        <div className="flex items-center justify-between p-4 sm:px-6">
          <div className="flex items-center space-x-3">
            <div className="rounded-lg bg-purple-100 p-2">
              <Bell className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">All Notifications</p>
              <p className="text-sm text-gray-500">
                Enable or disable all notifications
              </p>
            </div>
          </div>
          <button
            role="switch"
            aria-checked={notificationsEnabled}
            onClick={() => handleToggle('all')}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
              notificationsEnabled ? 'bg-purple-600' : 'bg-gray-200'
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                notificationsEnabled ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>

        {/* Push Notifications */}
        <div className="flex items-center justify-between p-4 sm:px-6">
          <div className="flex items-center space-x-3">
            <div className="rounded-lg bg-purple-100 p-2">
              <Bell className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Push Notifications</p>
              <p className="text-sm text-gray-500">
                Receive notifications in your browser
              </p>
            </div>
          </div>
          <button
            role="switch"
            aria-checked={pushNotificationsEnabled}
            onClick={() => handleToggle('push')}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
              pushNotificationsEnabled ? 'bg-purple-600' : 'bg-gray-200'
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                pushNotificationsEnabled ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>

        {/* Email Notifications */}
        <div className="flex items-center justify-between p-4 sm:px-6">
          <div className="flex items-center space-x-3">
            <div className="rounded-lg bg-purple-100 p-2">
              <Mail className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Email Notifications</p>
              <p className="text-sm text-gray-500">
                Receive notifications via email
              </p>
            </div>
          </div>
          <button
            role="switch"
            aria-checked={emailNotificationsEnabled}
            onClick={() => handleToggle('email')}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
              emailNotificationsEnabled ? 'bg-purple-600' : 'bg-gray-200'
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                emailNotificationsEnabled ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
      </div>
    </div>
  );
}